import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import TransactionsCharts from './pages/TransactionsCharts';
import CollectionsPage from './pages/CollectionsPage';
import LaptopStatisticsPage from './pages/LaptopStatisticsPage';
import LabPage from './pages/LabPage';
import GroupStudyRoomsPage from './pages/GroupStudyRoomsPage';
import Header from './components/Header';
import Footer from './components/Footer';
import SubHeader from './components/SubHeader';
import { fetchData } from './services/api';
import { formatData } from './services/dataFormatter';
import TitleHeader from './components/TitleHeader';

const AppRoutes = () => {
  const location = useLocation();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const pageTitles = {
    '/transaction-data': 'Transaction Data',
    '/collections-data': 'Collections Data - (Academic Year: 2024 - 2025)',
    '/laptop-statistics': 'Laptop Statistics',
    '/lab-data': 'Digital Arts and Humanities Lab',
    '/group-study-rooms': 'Group Study Rooms',
  };

  const currentTitle = pageTitles[location.pathname];

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setError('Please enter both start and end dates.');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) {
      setError('End date cannot be before the start date.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let data = await fetchData(startDate, endDate);
      data = formatData(data);
      setChartData(data);
    } catch (err) {
      setError('Error fetching data.');
    } finally {
      setLoading(false);
    }
  };

  // Pages that require the SubHeader (date range picker)
  const showSubHeaderOnPaths = [
    '/transaction-data',
    '/lab-data',
    '/group-study-rooms',
  ];

  const showSubHeader = showSubHeaderOnPaths.includes(location.pathname);

  return (
    <>
      {currentTitle && <TitleHeader title={currentTitle} />}
      {showSubHeader && (
        <SubHeader
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          handleGenerate={handleGenerate}
          loading={loading}
        />
      )}
      <Routes>
        <Route path="/" element={<Navigate to="/collections-data" replace />} />
        <Route
          path="/transaction-data"
          element={
            <TransactionsCharts
              chartData={chartData}
              loading={loading}
              error={error}
            />
          }
        />
        <Route path="/collections-data" element={<CollectionsPage />} />
        <Route
          path="/laptop-statistics"
          element={
            <LaptopStatisticsPage
              chartData={chartData}
              loading={loading}
              error={error}
            />
          }
        />
        <Route
          path="/lab-data"
          element={
            <LabPage
              startDate={startDate}
              endDate={endDate}
              loading={loading}
              error={error}
              setError={setError}
              setLoading={setLoading}
            />
          }
        />
        <Route
          path="/group-study-rooms"
          element={
            <GroupStudyRoomsPage
              startDate={startDate}
              endDate={endDate}
              loading={loading}
              error={error}
              setError={setError}
              setLoading={setLoading}
            />
          }
        />
      </Routes>
    </>
  );
};

const App = () => {
  return (
    <div className="app-wrapper">
      <Router basename="/statistics">
        <Header />
        <div className="content">
          <AppRoutes />
        </div>
        <Footer />
      </Router>
    </div>
  );
};

export default App;
